import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type JobRetryInputDTO, type JobRetryOperationDTO, type JobRetryPreviewDTO, type JobRetrySelectionDTO } from './api';
import { formatDate, Modal } from './components';
import { CategoryBrandFields } from './category-brand-fields';
import type { DxmCategoryNodeDTO } from '@wberp/shared';

const reasonLabels: Record<string, string> = {
  JOB_NOT_FAILED: '任务已不再失败或受阻',
  JOB_CHANGED: '任务状态已变化',
  JOB_NOT_FOUND: '任务不存在',
  JOB_ALREADY_ACTIVE: '已有同一商品或店铺的任务正在排队或执行',
  PRODUCT_UNAVAILABLE: '商品不可用',
  IDENTITY_INACTIVE: '账号身份未启用',
  SHOP_NOT_ACTIVE: '店铺未启用',
  SHOP_UNBOUND: '原店铺已解绑，请重新授权同一 WB 卖家后恢复旧任务',
  WB_CARD_REJECTION_REQUIRES_RESOLUTION: 'WB 建卡拒绝或货号冲突需处理',
  WB_CARD_BATCH_IN_PROGRESS: '商品卡仍在处理中',
  RETRY_PREPARATION_FAILED: '重试准备失败',
  WB_VENDOR_CODE_CONFLICT: 'WB 货号冲突',
  WB_CARD_CREATE_REJECTED: 'WB 拒绝创建商品卡',
  IDENTITY_RETRY_EXHAUSTED: '账号身份重试次数已用尽',
  AI_RETRY_EXHAUSTED: 'AI 文本处理重试次数已用尽',
  AI_AUTH_INVALID: 'AI 授权或访问被拒绝，请更新对应授权',
  AI_SERVICE_WAIT: 'AI 服务暂不可用，等待恢复',
  AI_PROVIDER_LIMIT_WAIT: 'AI 服务额度受限，等待恢复',
  AI_PAYLOAD_TOO_LARGE: 'AI 请求内容超过服务商限制',
  AI_IMAGE_RETRY_EXHAUSTED: '图片生成重试次数已用尽',
  AI_IMAGE_TIMEOUT: '图片生成超时',
  AI_IMAGE_UNAVAILABLE: '图片生成服务暂时不可用',
  AI_IMAGE_REFERENCE_UNAVAILABLE: '参考图片无法读取或处理',
  AI_IMAGE_CANCELLED: '图片生成已取消',
  AI_IMAGE_PLAN_REJECTED: '图片方案未通过校验',
  AI_IMAGE_REJECTED: '图片生成请求被拒绝',
  IMAGE_DOWNLOAD_UNAVAILABLE: '商品图片暂时无法下载',
  IMAGE_DOWNLOAD_TIMEOUT: '商品图片下载超时',
  IMAGE_DOWNLOAD_REJECTED: '商品图片下载地址无效或被拒绝',
  OSS_UPLOAD_UNAVAILABLE: '图片存储服务暂时不可用',
  NO_SELLABLE_SKUS: '没有可售规格',
  NO_SKU_CANDIDATES: '没有符合条件的规格',
  INTEGRATION_ENDPOINT_UNAVAILABLE: '外部服务地址暂时不可用',
  WB_VARIANT_LABELS_INVALID: 'WB 标题或规格不符合要求',
  WB_VARIANT_CHARACTERISTICS_INVALID: 'WB 规格属性不完整或无效',
  WB_REQUIRED_CHARACTERISTIC_MISSING: 'WB 缺少必填商品属性',
  WB_VENDOR_CODE_LENGTH_INVALID: 'WB 货号长度不符合要求',
  WB_VENDOR_CODE_SCHEME_INVALID: 'WB 货号规则无效',
  WB_COLOR_MAPPING_INVALID: 'WB 颜色映射无效',
  WB_CARD_FROZEN_PLAN_INVALID: 'WB 商品卡方案需要修复',
  WB_FROZEN_PLAN_REQUIRES_REPAIR: 'WB 历史商品卡内容需要修复',
  WB_RETRY_EXHAUSTED: 'WB 请求重试次数已用尽',
  WB_NETWORK_WAIT: 'WB 网络暂不可用，等待自动恢复',
  SHOP_BINDING_ARCHIVED: '旧店铺任务已封存，等待同卖家授权恢复',
  SHOP_BINDING_SUPERSEDED: '同源新发布优先，旧任务继续封存',
  WB_INVENTORY_RECOVERY_CHECK: '正在核对 WB 原卡库存回执',
  WB_INVENTORY_PROPAGATION_WAIT: '等待 WB 新卡同步到库存服务',
  WB_INVENTORY_RECEIPTS_REFRESHED: '原卡尺码回执已刷新，将继续库存步骤',
  WB_INVENTORY_CARD_UNAVAILABLE: 'WB 当前未返回原卡，请核对原卡后仅重试库存',
  WB_INVENTORY_CARD_TRASHED: 'WB 原卡在回收站，恢复原卡后仅重试库存',
  WB_INVENTORY_IDENTITY_CHANGED: '原卡身份或条码变化，已保留回执等待核对',
  WB_INVENTORY_RECEIPT_CONFLICT: '原卡回执已变化，请仅重试库存重新核对',
  WB_CARD_OTHER_BINDING_PENDING: '同卖家另一绑定正在确认该货号，稍后自动检查',
  WB_CARD_OTHER_BINDING_EXISTS: '同卖家另一绑定已发布该货号，已停止重复建卡',
  WB_UNAVAILABLE: 'WB 服务暂时不可用',
  WB_REQUEST_TIMEOUT: 'WB 请求超时',
  JOB_RETRY_BRAND_REQUIRED: '请先填写品牌，再处理品牌错误任务',
  JOB_RETRY_BRAND_INVALID: '替换品牌格式不正确',
  PRODUCT_NOT_FOUND: '商品不存在或已删除',
  PUBLISH_REPAIR_BUSY: '发布任务仍在运行，暂时不能修复',
  PUBLISH_REPAIR_SKU_CHANGED: '修复期间不能更改规格集合',
  PUBLISH_REPAIR_ALREADY_REQUESTED: '内容修复已安排，可重试相关任务',
  PUBLISH_REPAIR_EXTERNAL_RESULT_PENDING: 'WB 商品卡结果尚未确认',
  PUBLISH_REPAIR_CARD_STATE_UNSAFE: '商品卡状态不完整，暂不能重建内容',
  PUBLISH_RETRY_CONFIRMED_CARDS_REUSED: 'WB 商品卡已全部确认，复用现有卡片继续处理；新品牌或类目未应用到已建卡',
  PUBLISH_RETRY_CONFIRMED_VARIANTS_PRESERVED: '部分 WB 商品卡已确认，会保留现有卡片；新品牌或类目只用于重建未确认规格',
};

function reasonText(reason: string): string {
  return reasonLabels[reason] ?? '其他失败原因';
}

function reasonCodeLabel(code: string): string {
  return code === '' ? '未记录错误代码' : code;
}

function replacementBrandValue(value: string): string | null {
  if (!value.trim()) return null;
  if (/[\p{Cc}\p{Cf}]/u.test(value)) return null;
  const normalized = value.normalize('NFC').replace(/\s+/gu, ' ').trim();
  if (!normalized || [...normalized].length > 20 || /^(?:no\s*brand|无品牌|none|n\/a)$/iu.test(normalized)) return null;
  return normalized;
}

function isIdentityAvailabilityError(error: Error): boolean {
  const code = (error as ApiError).code;
  return code === 'IDENTITY_STATUS_UNAVAILABLE'
    || code === 'IDENTITY_BRIDGE_UNAVAILABLE'
    || /Identity status verification is temporarily unavailable|身份服务未配置/u.test(error.message);
}

function isTerminal(operation: JobRetryOperationDTO): boolean {
  return operation.status === 'completed';
}

function mergeOperation(items: JobRetryOperationDTO[], updated: JobRetryOperationDTO): JobRetryOperationDTO[] {
  const previous = items.find((item) => item.id === updated.id);
  if (previous && (previous.processed > updated.processed || isTerminal(previous) && !isTerminal(updated))) return items;
  return [updated, ...items.filter((item) => item.id !== updated.id)].slice(0, 5);
}

export function JobRetryControls({
  queue,
  selection,
  onCompleted,
  queueError = null,
  onRetryQueue,
  actionMessage = null,
}: {
  queue: JobRetryInputDTO['queue'];
  selection: JobRetrySelectionDTO;
  onCompleted?: () => void;
  queueError?: Error | null;
  onRetryQueue?: () => void;
  actionMessage?: string | null;
}) {
  const [operations, setOperations] = useState<JobRetryOperationDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [dialog, setDialog] = useState<{ kind: 'all' | 'selected'; input: JobRetryInputDTO } | null>(null);
  const [preview, setPreview] = useState<JobRetryPreviewDTO | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<Error | null>(null);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [replacementBrand, setReplacementBrand] = useState('');
  const [replacementSubjectId, setReplacementSubjectId] = useState<number | undefined>();
  const [replacementCategoryPath, setReplacementCategoryPath] = useState<DxmCategoryNodeDTO[]>([]);
  const [lockedPreview, setLockedPreview] = useState<JobRetryPreviewDTO | null>(null);
  const [pendingRequest, setPendingRequest] = useState<{ kind: 'all' | 'selected'; input: JobRetryInputDTO } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [pollError, setPollError] = useState<Error | null>(null);
  const [statusDetailsOpen, setStatusDetailsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const completedIds = useRef(new Set<string>());
  const mounted = useRef(false);
  const listVersion = useRef(0);
  const previewVersion = useRef(0);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const reloadRecent = useCallback(async () => {
    const version = ++listVersion.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await api.getJobRetries(queue);
      if (!mounted.current || version !== listVersion.current) return;
      const recent = result.items.slice(0, 5);
      setOperations(recent);
      for (const operation of recent) {
        if (isTerminal(operation)) completedIds.current.add(operation.id);
      }
    } catch (reason) {
      if (mounted.current && version === listVersion.current) setLoadError(reason instanceof Error ? reason : new Error('读取批量重试进度失败'));
    } finally {
      if (mounted.current && version === listVersion.current) setLoading(false);
    }
  }, [queue]);

  useEffect(() => {
    void reloadRecent();
  }, [reloadRecent, refreshKey]);

  const activeOperation = operations.find((operation) => !isTerminal(operation));

  async function loadRetryPreview(input: Pick<JobRetryInputDTO, 'queue' | 'selection'>, presetCodes?: string[]): Promise<void> {
    const version = ++previewVersion.current;
    if (!presetCodes) setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const value = await api.previewJobRetry(input);
      if (!mounted.current || version !== previewVersion.current) return;
      setPreview(value);
      if (presetCodes) setSelectedReasons(presetCodes);
      else setSelectedReasons(value.reasons.map((reason) => reason.errorCode));
    } catch (reason) {
      if (mounted.current && version === previewVersion.current) setPreviewError(reason instanceof Error ? reason : new Error('读取失败原因预览失败'));
    } finally {
      if (mounted.current && version === previewVersion.current) setPreviewLoading(false);
    }
  }

  function closeDialog(): void {
    if (submitting) return;
    previewVersion.current++;
    setDialog(null);
    setPreviewError(null);
    setPreviewLoading(false);
    if (!pendingRequest) {
      setPreview(null);
      setSelectedReasons([]);
      setLockedPreview(null);
      setReplacementBrand('');
      setReplacementSubjectId(undefined);
      setReplacementCategoryPath([]);

    }
  }

  useEffect(() => {
    if (!activeOperation) return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const updated = await api.getJobRetry(activeOperation.id);
        if (!active || !mounted.current) return;
        setOperations((current) => mergeOperation(current, updated));
        setPollError(null);
        if (isTerminal(updated) && !completedIds.current.has(updated.id)) {
          completedIds.current.add(updated.id);
          onCompletedRef.current?.();
          return;
        }
      } catch (reason) {
        if (!active || !mounted.current) return;
        setPollError(reason instanceof Error ? reason : new Error('刷新批量重试进度失败'));
      }
      if (active) timer = setTimeout(() => { void poll(); }, 2000);
    };
    timer = setTimeout(() => { void poll(); }, 2000);
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [activeOperation?.id, activeOperation?.status, queue]);

  useEffect(() => {
    if (!statusDetailsOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !statusRef.current?.contains(event.target)) setStatusDetailsOpen(false);
    };
    const closeEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setStatusDetailsOpen(false); };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [statusDetailsOpen]);

  function openRetry(kind: 'selected' | 'all'): void {
    if (submitting) return;
    if (pendingRequest) {
      setDialog(pendingRequest);
      setReplacementSubjectId(pendingRequest.input.subjectId);
      setReplacementBrand(pendingRequest.input.brand ?? '');

      void loadRetryPreview(pendingRequest.input, pendingRequest.input.errorCodes ?? []);
      return;
    }
    setLockedPreview(null);
    setReplacementBrand('');
    setReplacementSubjectId(undefined);
    setReplacementCategoryPath([]);

    const input: JobRetryInputDTO = {
      queue,
      idempotencyKey: crypto.randomUUID(),
      selection: kind === 'all'
        ? { allMatching: true, filter: {} }
        : { ...selection, ...(selection.ids ? { ids: [...selection.ids] } : {}), ...(selection.filter ? { filter: { ...selection.filter } } : {}) },
    };
    setDialog({ kind, input });
    void loadRetryPreview(input);
    setSubmitError(null);
  }

  async function submit(kind: 'all' | 'selected', input: JobRetryInputDTO): Promise<void> {
    if (submitting) return;
    const needsBrand = preview?.reasons.some((reason) => reason.requiresBrand && selectedReasons.includes(reason.errorCode));
    const brand = replacementBrandValue(replacementBrand);
    if (!pendingRequest && needsBrand && replacementBrand.trim() && !brand) {
      setSubmitError(new Error('品牌名称格式不正确，请修改或留空使用默认品牌。'));
      return;
    }
    const request = pendingRequest?.input ?? { ...input, errorCodes: [...selectedReasons], ...(needsBrand ? { ...(brand ? { brand } : {}), ...(replacementSubjectId ? { subjectId: replacementSubjectId } : {}) } : {}) };
    setSubmitting(true);
    setSubmitError(null);
    if (!pendingRequest) setLockedPreview(preview);
    setPendingRequest({ kind, input: request });
    try {
      const accepted = await api.createJobRetry(request);
      if (!mounted.current) return;
      listVersion.current++;
      setLoading(false);
      setLoadError(null);
      setOperations((current) => mergeOperation(current, accepted));
      setDialog(null);
      setPendingRequest(null);
      setLockedPreview(null);
      setPollError(null);
      if (isTerminal(accepted) && !completedIds.current.has(accepted.id)) {
        completedIds.current.add(accepted.id);
        onCompletedRef.current?.();
      }
    } catch (reason) {
      if (mounted.current) {
        setSubmitError(reason instanceof Error ? reason : new Error('提交批量重试失败'));
        if (reason instanceof ApiError && reason.status >= 400 && reason.status < 500) {
          setPendingRequest(null);
          setLockedPreview(null);
          setDialog((current) => current ? { ...current, input: { ...current.input, idempotencyKey: crypto.randomUUID() } } : current);
        }
      }
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }

  async function refreshProgress(): Promise<void> {
    const target = activeOperation ?? operations[0];
    if (!target) { setRefreshKey((value) => value + 1); return; }
    try {
      const updated = await api.getJobRetry(target.id);
      if (!mounted.current) return;
      setOperations((current) => mergeOperation(current, updated));
      setPollError(null);
      if (!isTerminal(target) && isTerminal(updated) && !completedIds.current.has(updated.id)) {
        completedIds.current.add(updated.id);
        onCompletedRef.current?.();
      }
    } catch (reason) {
      if (mounted.current) setPollError(reason instanceof Error ? reason : new Error('刷新批量重试进度失败'));
    }
  }

  const hasSelection = Boolean(selection.allMatching || selection.ids?.length);
  const title = dialog?.kind === 'all' ? '全部失败重试' : '批量重试失败项';
  const lockedCodes = pendingRequest?.input.errorCodes ?? [];
  const selectedCodes = pendingRequest ? lockedCodes : selectedReasons;
  const selectedCount = (pendingRequest ? lockedPreview : preview)?.reasons.reduce((total, reason) => selectedCodes.includes(reason.errorCode) ? total + reason.count : total, 0) ?? 0;
  const displayedReasons = pendingRequest
    ? lockedCodes.map((errorCode) => {
      const current = preview?.reasons.find((reason) => reason.errorCode === errorCode);
      const previous = lockedPreview?.reasons.find((reason) => reason.errorCode === errorCode);
      return { ...(previous ?? current ?? { errorCode, count: 0 }), count: current?.count ?? previous?.count ?? 0, priorCount: !current && Boolean(previous) };
    })
    : preview?.reasons.map((reason) => ({ ...reason, priorCount: false })) ?? [];
  const normalizedBrand = replacementBrandValue(replacementBrand);
  const selectableReasons = displayedReasons.filter((reason) => !reason.requiresBrand || !replacementBrand.trim() || Boolean(normalizedBrand));
  const allReasonsSelected = selectableReasons.length > 0 && selectableReasons.every((reason) => selectedCodes.includes(reason.errorCode));
  const editingReasons = !pendingRequest && !submitting;
  const hasInvalidBrandSelection = displayedReasons.some((reason) => reason.requiresBrand && selectedCodes.includes(reason.errorCode) && Boolean(replacementBrand.trim()) && !normalizedBrand);
  const canConfirm = Boolean(dialog && !submitting && (pendingRequest || preview && !previewLoading && !previewError && selectedReasons.length > 0 && !hasInvalidBrandSelection));
  const statusIssues: Array<{ key: string; error: Error; identities: string[]; retries: Array<() => void> }> = [];
  const addStatusIssue = (key: string, error: Error | null, retry?: () => void) => {
    if (!error) return;
    const apiError = error as ApiError;
    const identities = [
      ...(apiError.code ? [`code:${apiError.code}`] : []),
      ...(error.message ? [`message:${error.message}`] : []),
    ];
    const duplicate = statusIssues.find((issue) => issue.identities.some((identity) => identities.includes(identity)));
    if (duplicate) {
      if (retry) duplicate.retries.push(retry);
      return;
    }
    statusIssues.push({ key, error, identities, retries: retry ? [retry] : [] });
  };
  addStatusIssue('queue', queueError, onRetryQueue);
  addStatusIssue('history', loadError, () => setRefreshKey((value) => value + 1));
  addStatusIssue('poll', pollError, () => { void refreshProgress(); });
  addStatusIssue('submit', pendingRequest && submitError ? submitError : null, () => openRetry(pendingRequest?.kind ?? 'all'));
  const latestOperation = operations[0];
  const hasIdentityIssue = statusIssues.some((issue) => isIdentityAvailabilityError(issue.error));
  const statusText = hasIdentityIssue
    ? '账号校验服务暂时连接不上'
    : pendingRequest && submitError
      ? '提交结果待确认'
      : queueError
        ? '队列暂时无法读取'
        : statusIssues.length > 0
        ? '重试进度暂不可用'
        : latestOperation
          ? `重试 ${latestOperation.processed}/${latestOperation.total} · 已入队 ${latestOperation.enqueued}`
          : loading
            ? '读取重试状态…'
            : null;
  return <>
    <div className="job-retry-controls">
      <button className="button" type="button" disabled={submitting} onClick={() => openRetry('all')}>{pendingRequest ? '继续提交上次操作' : '全部失败重试'}</button>
      {hasSelection && <button className="button button-small button-quiet" type="button" disabled={submitting} onClick={() => openRetry('selected')}>{pendingRequest ? '继续上次操作' : '批量重试失败项'}</button>}
      {actionMessage && <span className="job-retry-action-message" role="status">{actionMessage}</span>}
      {statusText && <div className="job-retry-status" ref={statusRef}>
        <button className={`job-retry-status-trigger${statusIssues.length ? ' has-error' : ''}`} type="button" aria-expanded={statusDetailsOpen} onClick={() => setStatusDetailsOpen((open) => !open)}>{statusText}<span aria-hidden="true">⌄</span></button>
        {statusDetailsOpen && <div className="job-retry-status-popover" role="dialog" aria-label="队列状态和重试进度">
          <div className="job-retry-status-popover-head"><strong>{statusIssues.length ? '读取状态时遇到问题' : '最近的批量重试'}</strong><button className="button button-icon button-quiet" type="button" aria-label="关闭详情" onClick={() => setStatusDetailsOpen(false)}>×</button></div>
          {statusIssues.map((issue) => <div className="job-retry-status-issue" key={issue.key}>
            <div className="job-retry-status-error" title={issue.error.message}>{issue.error.message}</div>
            {(issue.error as ApiError).errorId && <small>问题编号：{(issue.error as ApiError).errorId}</small>}
            {issue.retries.length > 0 && <button className="button button-small" type="button" onClick={() => issue.retries.forEach((retry) => retry())}>重新读取</button>}
          </div>)}
          {operations.map((operation) => <div className="job-retry-item" key={operation.id}>
            <div className="job-retry-summary"><strong>{operation.status === 'running' ? '正在处理' : operation.status === 'completed' ? '已处理完成' : '等待后台处理'}</strong><span>{operation.processed} / {operation.total}</span><span>已入队 {operation.enqueued}</span><span>跳过 {operation.skipped}</span><span>处理失败 {operation.failed}</span></div>
            {Object.entries(operation.reasons).length > 0 && <div className="job-retry-reasons">处理说明或未入队原因：{Object.entries(operation.reasons).map(([reason, count]) => `${reasonText(reason)} ${count}`).join('；')}</div>}
            <div className="field-help">{formatDate(operation.createdAt)} · 入队不代表业务处理或发布已经成功。</div>
          </div>)}
          {loading && operations.length === 0 && <div className="field-help">正在读取最近的批量重试操作…</div>}
          {!statusIssues.length && !operations.length && !loading && <div className="field-help">暂无批量重试记录。</div>}
          <div className="job-retry-status-footer"><button className="button button-small button-quiet" type="button" disabled={loading} onClick={() => setRefreshKey((value) => value + 1)}>{loading ? '读取中…' : '刷新进度'}</button></div>
        </div>}
      </div>}
    </div>
    {dialog && <Modal
      title={title}
      description={dialog.kind === 'all' ? '扫描当前队列和账号权限内所有失败、受阻任务，包含其他分页。' : '仅检查所选任务或当前筛选范围内的失败、受阻任务。'}
      onClose={closeDialog}
      size="large"
      actions={<>
        <button className="button" type="button" disabled={submitting} onClick={closeDialog}>稍后</button>
        <button className="button button-primary" type="button" disabled={!canConfirm} onClick={() => void submit(dialog.kind, dialog.input)}>{submitting ? '提交中…' : pendingRequest && submitError ? '同一批次继续提交' : '确认入队'}</button>
      </>}
    >
      <div className="job-retry-preview-summary">
        <strong>{pendingRequest ? '本次提交已固定原因和批次' : `预计检查 ${preview?.total ?? '—'} 条失败或受阻任务`}</strong>
        <span>{pendingRequest ? `固定选择 ${lockedCodes.length} 种原因，提交前约 ${selectedCount} 条任务` : `已选择 ${selectedReasons.length} / ${preview?.reasons.length ?? '—'} 种原因，共 ${selectedCount} 条任务`}</span>
      </div>
      <p className="job-retry-preview-note">类目和品牌均可留空，沿用任务原有配置；可单独修改类目后重试。支持修复的建卡内容会先修复再重试。超时任务可单独选择，重试仍可能因连接或商品资料问题失败。提交结果未确认时，继续提交沿用同一批次。</p>
      {pendingRequest && preview && !previewLoading && !previewError && <div className="field-help">当前预览匹配 {preview.total} 条任务；续交仍使用提交前固定的原因和批次。</div>}
      {pendingRequest && (previewLoading || previewError) && <div className="field-help">以下原因与数量来自提交前预览，原请求内容保持固定。</div>}
      {previewLoading && <div className="field-help" role="status">正在读取失败原因和任务数量…</div>}
      {previewError && <div className="job-retry-poll-error" role="alert">{previewError.message}<button className="button button-small" type="button" disabled={previewLoading || submitting} onClick={() => void loadRetryPreview(dialog.input, pendingRequest?.input.errorCodes ?? undefined)}>重新读取</button></div>}
      {(preview || pendingRequest) && <div className="job-retry-reason-list" aria-label="失败原因列表">
        <label className="job-retry-reason-select-all">
          <input type="checkbox" className="check" checked={allReasonsSelected} disabled={!editingReasons || selectableReasons.length === 0} onChange={(event) => setSelectedReasons(event.currentTarget.checked ? selectableReasons.map((reason) => reason.errorCode) : [])} />
          <span>{pendingRequest ? '本次固定原因' : '全选可重试原因'}</span><span className="job-retry-reason-count">{pendingRequest ? `${lockedCodes.length} 种` : `${preview?.total ?? 0} 条`}</span>
        </label>
        <div className="job-retry-reason-scroll">
          {displayedReasons.map((reason, index) => <div className="job-retry-reason-row" key={reason.errorCode}>
            <div className="job-retry-reason-copy">
              <label htmlFor={`job-retry-reason-${index}`}>{reasonText(reason.errorCode)}</label>
              {!reasonLabels[reason.errorCode] && <small>{reasonCodeLabel(reason.errorCode)}</small>}
              {reason.details?.map((detail) => <div className="job-retry-reason-detail" key={detail.message}>{detail.message}<span> · {detail.count} 条</span></div>)}
              {reason.requiresBrand && <div className="job-retry-brand-field">
                <CategoryBrandFields retry hideLabels subjectId={replacementSubjectId} selectedPath={replacementCategoryPath} brand={replacementBrand}
                  disabled={!editingReasons}
                  onCategoryChange={(category, path) => {
                    setReplacementSubjectId(category?.categoryId);
                    setReplacementCategoryPath(path);

                    setSubmitError(null);
                  }}
                  onBrandChange={(value) => {
                    setReplacementBrand(value);
                    setSubmitError(null);
                  }}
                  />
                <span className="field-help">仅调整本次选中的品牌错误任务；品牌留空沿用原任务默认配置，修改类目不会清空品牌。</span>
              </div>}
            </div>
            <span className="job-retry-reason-count">{reason.count}{reason.priorCount && <small className="job-retry-prior-count">提交前</small>}</span>
            <input id={`job-retry-reason-${index}`} type="checkbox" className="check" aria-label={`选择${reasonText(reason.errorCode)}，${reason.count} 条`} checked={selectedCodes.includes(reason.errorCode)} disabled={!editingReasons || Boolean(reason.requiresBrand && replacementBrand.trim() && !normalizedBrand)} onChange={(event) => { const checked = event.currentTarget.checked; setSelectedReasons((current) => checked ? [...current, reason.errorCode] : current.filter((code) => code !== reason.errorCode)); }} />
          </div>)}
          {displayedReasons.length === 0 && <div className="job-retry-reason-empty">{pendingRequest ? '原请求的失败原因选择仍会原样提交。' : '当前选择范围内没有符合条件的失败或受阻任务。'}</div>}
        </div>
      </div>}
      <p className="job-retry-preview-footnote">后台分批入队，入队结果不代表业务处理或发布已经成功。</p>
      {submitError && <div className="job-retry-poll-error" role="alert">{submitError.message}。{pendingRequest ? '再次提交会沿用相同原因和批次。' : '请检查原因选择后重新提交。'}</div>}
    </Modal>}
  </>;
}
